import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { catLabel, articleHref } from "@/lib/format";

export function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data); }
      catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;
  const go = (path) => { onClose(); setQ(""); navigate(path); };

  return (
    <div className="fixed inset-0 z-[100] bg-ink/40 backdrop-blur-sm" onClick={onClose} data-testid="search-overlay">
      <div className="bg-paper max-w-2xl mx-auto mt-24 mx-4 sm:mx-auto border border-hair shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-hair">
          <Search size={18} className="text-crimson" />
          <input ref={inputRef} data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles, companies, authors…"
            className="flex-1 bg-transparent outline-none text-lg text-ink" />
          <button onClick={onClose} data-testid="search-close"><X size={18} className="text-mutedink" /></button>
        </div>
        {results && (
          <div className="max-h-[60vh] overflow-auto p-2 no-scrollbar">
            {results.articles?.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-mutedink">Articles</div>}
            {results.articles?.map((a) => (
              <button key={a.id} onClick={() => go(articleHref(a))} className="w-full text-left px-3 py-2.5 hover:bg-subtle transition-colors">
                <div className="text-[10px] uppercase tracking-widest text-crimson font-bold">{catLabel(a.category)}</div>
                <div className="font-serif text-base text-ink">{a.headline}</div>
              </button>
            ))}
            {results.companies?.length > 0 && <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-mutedink">Companies</div>}
            {results.companies?.map((c) => (
              <button key={c.id} onClick={() => go(`/companies/${c.slug}`)} className="w-full text-left px-3 py-2 hover:bg-subtle transition-colors font-sans text-sm text-ink">{c.name}</button>
            ))}
            {results.authors?.length > 0 && <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-mutedink">Authors</div>}
            {results.authors?.map((au) => (
              <button key={au.id} onClick={() => go(`/authors/${au.slug}`)} className="w-full text-left px-3 py-2 hover:bg-subtle transition-colors font-sans text-sm text-ink">{au.name}</button>
            ))}
            {!results.articles?.length && !results.companies?.length && !results.authors?.length && (
              <div className="px-4 py-8 text-center text-mutedink text-sm">No results for "{q}"</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
